import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FiShare2, FiX, FiCheck, FiExternalLink } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { AnnouncementRow } from '../../../model/CommunicationsAnnouncementsResult';
import { XAccountResult } from '../../../model/SocialAccountsResult';
import api from '../../../api/api';
import { getExternalLink } from '../../stock/AnnouncementTable';
import { toast } from '../Toast';

interface Props {
    row: AnnouncementRow;
    onClose: () => void;
}

const X_LIMIT = 280;

// X counts every link as 23 characters, whatever its length
const xLength = (text: string) => text.replace(/https?:\/\/\S+/g, 'x'.repeat(23)).length;

const errorText = (e: any, fallback: string) => typeof e?.response?.data === 'string' && e.response.data ? e.response.data : fallback;

// spec "Create a social post" (2:53531); manual post to X only for now
const SocialPostModal: React.FC<Props> = ({ row, onClose }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [account, setAccount] = useState<XAccountResult | null | undefined>(undefined);
    const [text, setText] = useState(() => `${row.headline}\n\n${getExternalLink({ localPath: row.localPath })}\n\n#ASX $${stock.ticker}`);
    const [posting, setPosting] = useState(false);
    const [postedUrl, setPostedUrl] = useState('');
    const length = xLength(text);

    useEffect(() => {
        api().getSocialAccounts(stock.id).then(r => setAccount(r.x)).catch(() => setAccount(null));
    }, [stock.id]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const post = async () => {
        setPosting(true);
        try {
            const result = await api().postX(stock.id, text);
            setPostedUrl(result.url);
            toast.success('Posted to X');
        } catch (e) {
            toast.error(errorText(e, 'X didn’t take the post'));
        }
        setPosting(false);
    };

    return <div className="delete-announcement-overlay" onClick={onClose}>
        <div className="delete-announcement-modal social-post" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon"><FiShare2 /></span>
                <div className="t">
                    <strong>Create a Social Post</strong>
                    <span>{account ? `Posts to X as @${account.username}` : 'X (Twitter)'}</span>
                </div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>
            <div className="modal-body">
                {account === undefined && <div className="line">Checking the connected X account…</div>}
                {account === null && <div className="line">X isn’t connected for this client. <Link to="/client-settings/announcements/social">Connect an account</Link></div>}
                {account && !postedUrl && <>
                    <textarea rows={7} value={text} onChange={e => setText(e.target.value)} aria-label="What the post says" />
                    <span className={`count${length > X_LIMIT ? ' over' : ''}`}>{length} / {X_LIMIT}</span>
                </>}
                {postedUrl && <div className="line"><FiCheck /> Posted. <a href={postedUrl} target="_blank" rel="noopener noreferrer">View on X <FiExternalLink /></a></div>}
            </div>
            <div className="modal-footer">
                <button type="button" className="btn-keep" onClick={onClose}>{postedUrl ? 'Done' : 'Cancel'}</button>
                {account && !postedUrl && <button type="button" className="btn-post" onClick={post} disabled={posting || !text.trim() || length > X_LIMIT}>{posting ? 'Posting…' : 'Post to X'}</button>}
            </div>
        </div>
    </div>;
};

export default SocialPostModal;
